import React, { useState } from 'react';
import Button from '../../../../../shared/components/ui/Button';

const Section5 = ({
  mode,
  collapsedSections,
  renderSectionHeader,
  handleOpenReasonModal,
  dsoData,
  canAddReason,
  allReasonsData,
  isLoadingReasons,
  onSubmitCommentInline,
  isSubmittingComment,
  sectionNote
}) => {
  // Filter reasons for Lead Response Time topic
  const section5Reasons = Array.isArray(allReasonsData)
    ? allReasonsData.filter((reason) => reason.topic === 'Lead Response Time')
    : [];

  // Get the global reason for the section (first Lead Response Time reason)
  const globalReason = section5Reasons.length > 0 ? section5Reasons[0] : null;

  // State for inline editing
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentText, setCommentText] = useState(globalReason?.reason || '');

  // Track previous submitting state to detect when submission completes
  const prevIsSubmittingRef = React.useRef(isSubmittingComment);

  // Update commentText when globalReason changes and reset editing state after successful save
  React.useEffect(() => {
    if (globalReason?.reason) {
      setCommentText(globalReason.reason);
    } else {
      setCommentText('');
    }

    // Reset editing state when submission completes successfully
    if (prevIsSubmittingRef.current && !isSubmittingComment && isEditingComment) {
      setIsEditingComment(false);
    }
    prevIsSubmittingRef.current = isSubmittingComment;
  }, [globalReason?.reason, isSubmittingComment, isEditingComment]);

  const handleStartEditing = () => {
    setIsEditingComment(true);
    setCommentText(globalReason?.reason || '');
  };

  const handleCancelEditing = () => {
    setIsEditingComment(false);
    setCommentText(globalReason?.reason || '');
  };

  const handleSaveComment = () => {
    if (!commentText.trim()) {
      return;
    }
    // Pass reason ID if editing existing comment
    const reasonId = globalReason?.id || null;
    onSubmitCommentInline('Lead Response Time', commentText, reasonId);
    // Editing state will be reset in useEffect after successful save
  };

  // Helper function to convert hours to days, hours, and minutes
  const formatResponseTime = (hours) => {
    if (!hours || hours === 0) return '0m';

    const totalMinutes = Math.round(hours * 60);
    const days = Math.floor(totalMinutes / (24 * 60));
    const remainingMinutes = totalMinutes % (24 * 60);
    const hoursPart = Math.floor(remainingMinutes / 60);
    const minutesPart = remainingMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hoursPart > 0) parts.push(`${hoursPart}h`);
    if (minutesPart > 0) parts.push(`${minutesPart}m`);

    return parts.length > 0 ? parts.join(' ') : '0m';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {renderSectionHeader(5, 'Lead Response Time')}
      {(mode === 'view' || !collapsedSections[5]) && (
        <>
          <p className="text-sm text-gray-600 mb-4">Average response time for campaign leads</p>
          <div className="overflow-x-auto">
            {!Array.isArray(dsoData?.data?.leadResponseTime) ||
            dsoData?.data?.leadResponseTime?.length === 0 ? (
              <div className="flex justify-center items-center h-[8rem]">
                <p className="text-sm text-gray-500">No data available</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Campaign Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Avg Response Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Total Leads
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Array.isArray(dsoData?.data?.leadResponseTime) &&
                    dsoData.data.leadResponseTime.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatResponseTime(item.averageFollowUpCompletionTime)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.leadCount || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Comment Section - Bottom with better UI */}
          {Array.isArray(dsoData?.data?.leadResponseTime) &&
            dsoData.data.leadResponseTime.length > 0 && (
              <div className="mt-6 p-5 bg-gradient-to-r from-red-50 to-red-50 border border-red-200 rounded-lg shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {globalReason?.user?.vertical || 'Sales'} comment
                      </h4>
                      {globalReason?.user && !isEditingComment && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                          <span className="text-xs text-gray-600">By</span>
                          <span className="text-xs font-medium text-gray-900">
                            {globalReason.user.name || 'Unknown User'}
                          </span>
                        </div>
                      )}
                    </div>
                    {isLoadingReasons ? (
                      <p className="text-sm text-gray-500 italic">Loading comment...</p>
                    ) : isEditingComment ? (
                      <div className="space-y-3">
                        <textarea
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder="Enter your comment..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                          rows={4}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveComment}
                            displayType="secondary-red"
                            disabled={!commentText.trim() || isSubmittingComment}>
                            {isSubmittingComment ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            onClick={handleCancelEditing}
                            displayType="secondary"
                            disabled={isSubmittingComment}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : globalReason ? (
                      <div
                        onClick={
                          canAddReason && mode === 'dashboard' ? handleStartEditing : undefined
                        }
                        className={`text-sm text-gray-900 font-medium leading-relaxed ${
                          canAddReason && mode === 'dashboard'
                            ? 'cursor-pointer hover:bg-red-100 p-2 rounded transition-colors'
                            : ''
                        }`}>
                        {globalReason.reason}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No comment added yet</p>
                    )}
                  </div>
                  {/* Add Comment Button - always show when canAddReason is true and mode is dashboard */}
                  {canAddReason && mode === 'dashboard' && !isEditingComment && (
                    <div className="flex-shrink-0">
                      <Button
                        onClick={
                          globalReason ? handleStartEditing : () => setIsEditingComment(true)
                        }
                        displayType="secondary-red">
                        {globalReason ? 'Edit Comment' : 'Add Comment'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
        </>
      )}
      {/* Section Note */}
      {sectionNote && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  {sectionNote.user?.vertical || 'Management'} notes
                </h4>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-600">By</span>
                  <span className="text-xs font-medium text-gray-900">
                    {sectionNote.user?.name || 'Unknown User'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{sectionNote.note}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section5;
